import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Seo from '../components/Seo';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { copyrightYears } from '../lib/utils';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import { MessageSquare, Loader2, Mail } from 'lucide-react';
import MaintenanceModal from '../components/MaintenanceModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [sendingReset, setSendingReset] = useState(false);
  const [systemVersion, setSystemVersion] = useState('');
  const [maintenance, setMaintenance] = useState(null);
  const [maintenanceModalOpen, setMaintenanceModalOpen] = useState(false);
  // E-mail de quem tentou entrar sem ter confirmado o cadastro. Enquanto tiver valor,
  // a tela mostra o bloco explicando o passo que falta, no lugar do erro seco.
  const [emailNaoConfirmado, setEmailNaoConfirmado] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Vindo da tela "Link expirado" (set-password): abre o modal de recuperação já
  // preenchido com o e-mail da conta, para o usuário gerar um novo link num clique.
  // Limpa o state depois para o modal não reabrir em refresh/voltar.
  useEffect(() => {
    if (location.state?.openForgot) {
      setForgotPasswordOpen(true);
      if (location.state.email) setForgotEmail(location.state.email);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, navigate]);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/public/system-version`);
        if (res.ok) {
          const data = await res.json();
          if (data.version) setSystemVersion(data.version);
        }
      } catch {
        // silent — fallback to no display
      }
    })();
  }, []);

  // Check maintenance status on mount; open the modal automatically when active.
  // After "Entendido", modal closes and login form is usable — backend still blocks
  // non-Super-Admin logins with 503, in which case we reopen the modal.
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/public/maintenance-status`);
        if (res.ok) {
          const data = await res.json();
          if (data?.active) {
            setMaintenance(data);
            setMaintenanceModalOpen(true);
          }
        }
      } catch {
        // silent
      }
    })();
  }, []);

  const validateEmail = (email) => {
    // Regex para validar email com domínio completo (com ponto)
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation(); // Previne propagação do evento
    
    // Validar email antes de enviar
    if (!validateEmail(email)) {
      toast.error('E-mail inválido. Use o formato: nome@dominio.com');
      return;
    }
    
    setLoading(true);
    setEmailNaoConfirmado('');

    try {
      const userData = await login(email, password);
      toast.success('Login realizado com sucesso!');
      
      // Redirect based on role
      if (userData.role === 'SUPER_ADMIN') {
        navigate('/admins');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      // If backend says maintenance, reopen the maintenance modal (refresh status too)
      if (error?.response?.status === 503) {
        try {
          const res = await fetch(`${API_URL}/api/public/maintenance-status`);
          if (res.ok) {
            const data = await res.json();
            if (data?.active) setMaintenance(data);
          }
        } catch {/* silent */}
        setMaintenanceModalOpen(true);
        setPassword('');
        setLoading(false);
        return;
      }

      // Cadastro sem confirmação de e-mail: antes caía no 401 "Conta desabilitada", que soava
      // como banimento e fazia o cliente desistir em vez de abrir o e-mail. Agora vira um bloco
      // explicativo na própria tela, e não um toast vermelho que some em 3 segundos.
      const detalhe = error?.response?.data?.detail;
      if (error?.response?.status === 403 && detalhe?.code === 'email_not_confirmed') {
        setEmailNaoConfirmado(detalhe.email || email);
        setPassword('');
        setLoading(false);
        return;
      }

      let errorMessage = 'Erro ao fazer login';
      
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.message) {
        // Traduzir mensagens de erro comuns do navegador
        if (error.message.includes('body stream already read')) {
          errorMessage = 'Erro de conexão. Tente novamente.';
        } else if (error.message.includes('Network Error')) {
          errorMessage = 'Erro de conexão. Verifique sua internet.';
        } else {
          errorMessage = error.message;
        }
      }
      
      // Traduzir mensagens comuns
      if (errorMessage === 'Invalid credentials') {
        errorMessage = 'E-mail ou senha inválidos';
      }
      
      toast.error(errorMessage, {
        duration: 3000, // 3 segundos
      });
      
      // Limpa os campos após erro
      setPassword('');
      setEmail('');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    
    if (!forgotEmail) {
      toast.error('Digite seu email');
      return;
    }
    
    // Validar email antes de enviar
    if (!validateEmail(forgotEmail)) {
      toast.error('E-mail inválido. Use o formato: nome@dominio.com');
      return;
    }

    setSendingReset(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotEmail })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Erro ao enviar');
      }

      toast.success('Se o email existir, você receberá instruções de recuperação');
      setForgotPasswordOpen(false);
      setForgotEmail('');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSendingReset(false);
    }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center bg-gradient-to-br from-teal-50 via-teal-50 to-white p-4"
      style={{
        '--background': '210 33% 98%',
        '--foreground': '220 15% 10%',
        '--card': '0 0% 100%',
        '--card-foreground': '220 15% 10%',
        '--popover': '0 0% 100%',
        '--popover-foreground': '220 15% 10%',
        '--primary': '181 77% 24%',
        '--primary-foreground': '160 76% 97%',
        '--secondary': '210 25% 96%',
        '--secondary-foreground': '220 15% 15%',
        '--muted': '220 9% 46%',
        '--muted-foreground': '220 9% 46%',
        '--border': '214 17% 91%',
        '--input': '214 17% 91%',
        '--ring': '181 77% 24%',
      }}
    >
      <Seo
        title="Entrar | Watizap"
        description="Acesse sua conta Watizap para enviar mensagens e campanhas pelo WhatsApp."
        path="/login"
      />
      <Card className="w-full max-w-md bg-white" data-testid="login-form">
        <CardHeader className="text-center">
          <div className="w-16 h-16 rounded-xl bg-primary flex items-center justify-center mx-auto mb-4">
            <MessageSquare className="w-8 h-8 text-primary-foreground" />
          </div>
          <CardTitle className="text-2xl text-gray-900">Watizap</CardTitle>
          <CardDescription className="text-gray-500">Seu zap turbinado</CardDescription>
        </CardHeader>
        <CardContent>
          {emailNaoConfirmado && (
            <div
              className="mb-5 rounded-lg border border-amber-300 bg-amber-50 p-4 text-left dark:border-amber-900/50 dark:bg-amber-950/20"
              data-testid="login-email-nao-confirmado"
            >
              <div className="flex items-start gap-3">
                <Mail className="w-5 h-5 shrink-0 mt-0.5 text-amber-600 dark:text-amber-400" />
                {/* Só o aviso, sem botão: não há nada para fazer aqui dentro, e o bloco já
                    desaparece sozinho na próxima tentativa de login. */}
                <div className="space-y-2">
                  <p className="font-semibold text-amber-900 dark:text-amber-200">
                    Confirme seu e-mail
                  </p>
                  <p className="text-sm text-amber-900/80 dark:text-amber-200/80">
                    Sua conta só é ativada depois que você clica no link que enviamos para{' '}
                    <strong>{emailNaoConfirmado}</strong>. Abra a mensagem do Watizap e clique no
                    botão <strong>Confirmar Email</strong>. Se não encontrar, procure na caixa de
                    spam ou no lixo eletrônico.
                  </p>
                </div>
              </div>
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-gray-700">E-mail</Label>
              <Input
                id="email"
                type="email"
                placeholder="seu@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="login-email-input"
                className="bg-white border-gray-300 text-gray-900"
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-gray-700">Senha</Label>
                <button
                  type="button"
                  onClick={() => {
                    setForgotEmail(email);
                    setForgotPasswordOpen(true);
                  }}
                  className="text-sm text-teal-700 hover:underline"
                  data-testid="forgot-password-link"
                >
                  Esqueci minha senha
                </button>
              </div>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="login-password-input"
                className="bg-white border-gray-300 text-gray-900"
              />
            </div>
            <Button
              type="submit"
              className="w-full bg-teal-700 hover:bg-teal-800 text-white"
              disabled={loading}
              data-testid="login-submit-button"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Entrando...
                </>
              ) : (
                'Entrar'
              )}
            </Button>
          </form>
          <div className="mt-6 text-center text-sm text-gray-500">
            <p>© {copyrightYears()} • Todos os direitos reservados</p>
          </div>
        </CardContent>
      </Card>

      {/* Forgot Password Dialog */}
      <Dialog open={forgotPasswordOpen} onOpenChange={setForgotPasswordOpen}>
        <DialogContent className="sm:max-w-md bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-gray-900">
              <Mail className="w-5 h-5" />
              Recuperar Senha
            </DialogTitle>
            <DialogDescription className="text-gray-500">
              Digite seu email para receber instruções de recuperação de senha.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleForgotPassword}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="forgot-email" className="text-gray-700">Email</Label>
                <Input
                  id="forgot-email"
                  type="email"
                  placeholder="seu@email.com"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  required
                  data-testid="forgot-email-input"
                  className="bg-white border-gray-300 text-gray-900"
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setForgotPasswordOpen(false)}
                className="text-gray-700"
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={sendingReset} className="bg-teal-700 hover:bg-teal-800 text-white">
                {sendingReset ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Enviando...
                  </>
                ) : (
                  'Enviar'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      
      {/* Version */}
      <div className="fixed bottom-4 right-4 text-xs text-gray-500" data-testid="login-version">
        {systemVersion ? `version ${systemVersion}` : ''}
      </div>

      {/* Maintenance modal — open on mount when active, reopens after 503 login attempt */}
      <MaintenanceModal
        open={maintenanceModalOpen}
        status={maintenance}
        onClose={() => setMaintenanceModalOpen(false)}
      />
    </div>
  );
}
